// Migration runner (Implementation Plan v02 §6.3, CR-86, D-83; I-1a Batch 3).
//
// - One data-schema version for the store; most changes need none (§2.2).
// - When one is needed: expand first, contract one release later.
// - Before running: a copy of every sealed record goes into `premigration`
//   (already encrypted — it is the same ciphertext), with a note in `meta`. On
//   any failure the copy is restored and the old version stays; the store then
//   opens read-only. If even the copy cannot be taken (e.g. storage full), the
//   run stops with nothing changed.
// - A version that meets a NEWER database opens it read-only for export.
// - The write of the migrated records is one IndexedDB transaction, which also
//   clears the copy, so a deleted moment never lingers in `premigration`.
//
// IndexedDB transactions close themselves when the code awaits anything that is
// not an IndexedDB request (e.g. WebCrypto). So decrypt / transform / encrypt
// run BETWEEN transactions, never inside one.
//
// Because each step is one transaction, the restore is a safety net for a run
// that was interrupted (the note is left behind) or for a browser that does not
// honour atomicity; tests cover both the normal rollback and an interrupted run.
// I-1a Batch 7: MIGRATIONS below is the app's real list (the store's default),
// a synthetic fixture is kept per released schema version (tools/testdata, each
// migrated by every build) and the forced-failure run is check A9 on the device
// test page (two phones).

import type { SealedRecord } from '../crypto'

import { decodeRecord, type DiaryRecord, encodeRecord } from './codec'
import { META_DATA_SCHEMA, META_PREMIGRATION, type PremigrationNote, type StoreDb, watchDone } from './schema'

export interface Migration {
  readonly from: number
  readonly to: number
  /** Pure transform of one decrypted record; return null to drop it. */
  readonly migrate: (record: DiaryRecord) => DiaryRecord | null
}

/**
 * The app's migrations, one step per data-schema version: `from: n, to: n + 1`
 * for every n below DATA_SCHEMA_VERSION. Empty while version 1 is the only one.
 * A new step needs, in the same change: DATA_SCHEMA_VERSION moved up, a frozen
 * fixture for the version being left (tools/testdata) and its hash pinned in
 * tests/unit/fixtures.test.ts (that test fails the build otherwise).
 */
export const MIGRATIONS: readonly Migration[] = []

export type MigrationOutcome =
  | { readonly kind: 'fresh'; readonly version: number }
  | { readonly kind: 'up-to-date'; readonly version: number }
  | { readonly kind: 'migrated'; readonly from: number; readonly to: number; readonly skipped: number }
  | { readonly kind: 'newer-data'; readonly stored: number; readonly app: number }
  | { readonly kind: 'failed'; readonly from: number; readonly to: number }

/** The stored data-schema version, or null for a database that has none yet. */
export async function readStoredVersion(db: StoreDb): Promise<number | null> {
  const value = await db.get('meta', META_DATA_SCHEMA)
  return typeof value === 'number' ? value : null
}

function isPremigrationNote(value: unknown): value is PremigrationNote {
  return typeof value === 'object' && value !== null && typeof (value as PremigrationNote).fromVersion === 'number'
}

function planChain(migrations: readonly Migration[], from: number, to: number): Migration[] | null {
  const chain: Migration[] = []
  for (let v = from; v < to; v++) {
    const step = migrations.find((m) => m.from === v && m.to === v + 1)
    if (!step) {
      return null
    }
    chain.push(step)
  }
  return chain
}

async function backUp(db: StoreDb, note: PremigrationNote): Promise<void> {
  const tx = db.transaction(['records', 'premigration', 'meta'], 'readwrite')
  const done = watchDone(tx)
  const premigration = tx.objectStore('premigration')
  await premigration.clear()
  const copy = await tx.objectStore('records').getAll()
  await Promise.all([
    ...copy.map((sealed) => premigration.put(sealed)),
    tx.objectStore('meta').put(note, META_PREMIGRATION),
  ])
  await done
}

/**
 * Put the pre-migration copy back and keep the old version. Idempotent. Only
 * called when the note exists — the note is written in the same transaction as
 * the copy, so a note always means the copy is complete.
 */
export async function restoreFromPremigration(db: StoreDb, version: number): Promise<void> {
  const tx = db.transaction(['records', 'premigration', 'meta'], 'readwrite')
  const done = watchDone(tx)
  const records = tx.objectStore('records')
  const premigration = tx.objectStore('premigration')
  const meta = tx.objectStore('meta')
  const copy = await premigration.getAll()
  await records.clear()
  await Promise.all([
    ...copy.map((sealed) => records.put(sealed)),
    premigration.clear(),
    meta.put(version, META_DATA_SCHEMA),
    meta.delete(META_PREMIGRATION),
  ])
  await done
}

export async function runMigrations(
  db: StoreDb,
  dataKey: CryptoKey,
  appVersion: number,
  migrations: readonly Migration[],
): Promise<MigrationOutcome> {
  // A note left behind means a run was interrupted. Both the migrated write and
  // the restore are single transactions that clear the note, so `records` is
  // still the copy; restoring again is safe and makes that certain.
  const leftover = await db.get('meta', META_PREMIGRATION)
  if (isPremigrationNote(leftover)) {
    try {
      await restoreFromPremigration(db, leftover.fromVersion)
    } catch {
      return { kind: 'failed', from: leftover.fromVersion, to: appVersion } // retried on the next open
    }
  }

  let stored = await readStoredVersion(db)
  if (stored === null) {
    if ((await db.count('records')) === 0) {
      await db.put('meta', appVersion, META_DATA_SCHEMA)
      return { kind: 'fresh', version: appVersion }
    }
    stored = 1 // records written before the version was stamped: the first version
  }
  if (stored > appVersion) {
    return { kind: 'newer-data', stored, app: appVersion }
  }
  if (stored === appVersion) {
    return { kind: 'up-to-date', version: stored }
  }
  const from = stored

  const chain = planChain(migrations, from, appVersion)
  if (!chain) {
    return { kind: 'failed', from, to: appVersion } // nothing was changed
  }

  // The copy is one transaction: if it fails (e.g. storage full) nothing was
  // written, and restoring would be WRONG (there is no complete copy). Stop here.
  try {
    await backUp(db, { fromVersion: from, toVersion: appVersion })
  } catch {
    return { kind: 'failed', from, to: appVersion }
  }

  try {
    const sealedAll = (await db.getAll('records')).filter((r) => r.kind === 'record')
    const rewritten: SealedRecord[] = []
    const dropped: string[] = []
    let skipped = 0
    for (const sealed of sealedAll) {
      let record: DiaryRecord | null
      try {
        record = await decodeRecord(dataKey, sealed)
      } catch {
        skipped++ // a record that cannot be opened is kept as it is, never wiped
        continue
      }
      for (const step of chain) {
        if (record === null) {
          break
        }
        record = step.migrate(record)
      }
      if (record === null) {
        dropped.push(sealed.id)
      } else {
        if (record.id !== sealed.id) {
          throw new Error('a migration must not change a record id')
        }
        rewritten.push(await encodeRecord(dataKey, record, appVersion))
      }
    }
    if (sealedAll.length > 0 && skipped === sealedAll.length) {
      // Not one record opened: the key is wrong, not the data. Never stamp a new
      // version over records that were not migrated.
      throw new Error('no record could be opened; refusing to migrate')
    }

    const tx = db.transaction(['records', 'premigration', 'meta'], 'readwrite')
    const done = watchDone(tx)
    const records = tx.objectStore('records')
    const meta = tx.objectStore('meta')
    await Promise.all([
      ...rewritten.map((sealed) => records.put(sealed)),
      ...dropped.map((id) => records.delete(id)),
      tx.objectStore('premigration').clear(),
      meta.put(appVersion, META_DATA_SCHEMA),
      meta.delete(META_PREMIGRATION),
    ])
    await done
    return { kind: 'migrated', from, to: appVersion, skipped }
  } catch {
    try {
      await restoreFromPremigration(db, from)
    } catch {
      // The note stays; the next open restores first. `records` is unchanged
      // because the migrated write is a single transaction.
    }
    return { kind: 'failed', from, to: appVersion }
  }
}
